import { useCallback, useEffect, useMemo, useRef } from 'react'

import { Skeleton } from '@mantine/core'

import { navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_EMPLOYEE_FUNNELS } from 'src/graphql/pipelineMetrics.graphql'
import { ApplicantStage } from 'src/graphql/types/applicants'
import AnalyticsCard from 'src/pages/AnalyticsPage/components/AnalyticsCard'

import { buildApplicantsFilterUrl } from '../buildApplicantsFilterUrl'
import { STAGE_ORDER, STAGE_PASTEL } from '../OverviewTab/stagePalette'

import { buildEmployeeDisplayName } from './employeeDisplayName'
import EmployeeFunnelCard, {
  type EmployeeFunnelCardSegment,
} from './EmployeeFunnelCard'
import type { PerformanceFilters } from './PerformanceTab'

const TITLE = 'Employee Funnels'

const INFO_TEXT =
  "Per-employee stage breakdown for the selected date range. Each card shows the employee's avatar, role, and a five-row mini-funnel. Bars are normalised against the largest stage count across ALL employees, so cards are visually comparable side-by-side. Counts use each stage's own timestamp column (matches Pipeline Summary)."

// ---------------------------------------------------------------------------
// Local types — match the SDL `EmployeeFunnelRow` shape.
// ---------------------------------------------------------------------------
interface FunnelStageCountServer {
  stage: ApplicantStage
  count: number
}
interface FunnelServerRow {
  employeeId: string
  employee: {
    employeeId: string
    firstName: string | null
    lastName: string | null
    jobTitle: string | null
    coverImage: string | null
  }
  total: number
  stages: ReadonlyArray<FunnelStageCountServer>
}
interface FunnelServerResponse {
  employeeFunnels: ReadonlyArray<FunnelServerRow>
}

interface FunnelDisplayRow {
  id: string
  name: string
  jobTitle: string | null
  coverImage: string | null
  total: number
  /** Pre-built segments in fixed STAGE_ORDER, ready for the card to consume. */
  segments: ReadonlyArray<EmployeeFunnelCardSegment>
}

const GRID_STYLE: React.CSSProperties = {
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
}

const SKELETON_PLACEHOLDER_COUNT = 4

export interface EmployeeFunnelsContainerProps {
  /** Page-level FiltersBar state — the resolver honors every facet. */
  filters: PerformanceFilters
  refreshKey: number
}

const EmployeeFunnelsContainer = ({
  filters,
  refreshKey,
}: EmployeeFunnelsContainerProps) => {
  const { dateRange } = filters
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery<FunnelServerResponse>(
    GET_EMPLOYEE_FUNNELS,
    {
      variables: { input: { filters } },
      onError,
      notifyOnNetworkStatusChange: true,
    }
  )

  // Skip the initial mount — useQuery already kicked off the first fetch.
  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const rows: ReadonlyArray<FunnelDisplayRow> = useMemo(() => {
    const serverRows = data?.employeeFunnels ?? []
    return serverRows.map((row) => {
      // Index server stages by stage so we can walk STAGE_ORDER and produce
      // segments in the same fixed visual order as the Pipeline Summary card,
      // even if the server returned them in a different order.
      const byStage = new Map(row.stages.map((s) => [s.stage, s.count]))
      const segments: EmployeeFunnelCardSegment[] = STAGE_ORDER.map(
        (stageDef) => ({
          id: stageDef.stage,
          stage: stageDef.stage,
          count: byStage.get(stageDef.stage) ?? 0,
          color: STAGE_PASTEL[stageDef.stage],
          label: stageDef.label,
        })
      )
      return {
        id: row.employeeId,
        name: buildEmployeeDisplayName(
          row.employee.firstName,
          row.employee.lastName,
          row.employeeId
        ),
        jobTitle: row.employee.jobTitle,
        // Server returns "" for blank coverImage values — coerce to null so
        // the card's avatar fallback (initials) kicks in consistently.
        coverImage:
          row.employee.coverImage && row.employee.coverImage.length > 0
            ? row.employee.coverImage
            : null,
        total: row.total,
        segments,
      }
    })
  }, [data])

  // Global max across every (employee × stage) so bar widths in one card are
  // directly comparable to bars in any other card. Without this, each card
  // self-normalises and the leading stage always reads as 100%.
  const globalMaxCount = useMemo(() => {
    let max = 0
    for (const row of rows) {
      for (const seg of row.segments) {
        if (seg.count > max) max = seg.count
      }
    }
    return max
  }, [rows])

  // Loading state: render a grid of skeleton cards so the page doesn't jump
  // when results come in. Only show on first load (no data yet) — refetches
  // keep the existing rows visible.
  const isInitialLoad = loading && !data
  if (isInitialLoad) {
    return (
      <AnalyticsCard title={TITLE} info={INFO_TEXT}>
        <div
          className="grid gap-3"
          style={GRID_STYLE}
          data-testid="employee-funnels-loading"
        >
          {Array.from({ length: SKELETON_PLACEHOLDER_COUNT }).map((_, idx) => (
            <div
              key={idx}
              className="flex flex-col gap-3 rounded-md border border-doubleNickel-gray-200 bg-white p-3.5"
            >
              <div className="flex items-center gap-2.5">
                <Skeleton circle height={40} width={40} />
                <div className="flex-1">
                  <Skeleton height={12} width="60%" mb={6} />
                  <Skeleton height={8} width="40%" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {STAGE_ORDER.map((stageDef) => (
                  <Skeleton key={stageDef.stage} height={10} radius="sm" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </AnalyticsCard>
    )
  }

  if (rows.length === 0) {
    return (
      <AnalyticsCard title={TITLE} info={INFO_TEXT}>
        <div className="py-6 text-center text-sm text-gray-500">
          No applicants in this period.
        </div>
      </AnalyticsCard>
    )
  }

  return (
    <AnalyticsCard title={TITLE} info={INFO_TEXT}>
      <div
        className="grid gap-3"
        style={GRID_STYLE}
        data-testid="employee-funnels-grid"
      >
        {rows.map((row) => (
          <EmployeeFunnelCard
            key={row.id}
            name={row.name}
            jobTitle={row.jobTitle}
            coverImage={row.coverImage}
            total={row.total}
            segments={row.segments}
            globalMaxCount={globalMaxCount}
            // Header → applicants the employee touched in the period (no
            // stage filter). Segments → per-stage slice using stage-event
            // semantics, matching the resolver's INFO_TEXT note that
            // "counts use each stage's own timestamp column".
            onTotalClick={() =>
              navigate(
                buildApplicantsFilterUrl({
                  dateRange,
                  recruiterIds: [row.id],
                  jobListingIds: filters.jobListings,
                  leadSources: filters.linkTypes,
                })
              )
            }
            onSegmentClick={(stage) =>
              navigate(
                buildApplicantsFilterUrl({
                  stage,
                  dateRange,
                  recruiterIds: [row.id],
                  jobListingIds: filters.jobListings,
                  leadSources: filters.linkTypes,
                })
              )
            }
          />
        ))}
      </div>
    </AnalyticsCard>
  )
}

export default EmployeeFunnelsContainer
